import { useState, useMemo } from 'react';
import { Modal, Button } from './ui';
import { aplicarVariaciones } from '../utils/helpers';
import VariacionFormModal from './VariacionFormModal';
import { Plus, Pencil, X, TrendingDown, TrendingUp } from 'lucide-react';

/**
 * Modal principal de variaciones de precio. Muestra la lista aplicada + preview
 * acumulado + botón para agregar/editar/eliminar. Persiste todo en el state
 * del padre (no hace requests al backend — se guardan cuando la cotización
 * completa se guarda).
 *
 * Props:
 *   open        → boolean
 *   onClose     → cerrar
 *   base        → number, valor base sobre el que se aplican todas las variaciones
 *   variaciones → array actual (viene del state padre)
 *   onChange    → callback(nuevoArray) cada vez que se modifica la lista
 *   titulo      → texto descriptivo del scope (ej. "Personal — Diego")
 *   moneda      → 'PEN' | 'USD'
 *   readOnly    → si true, deshabilita agregar/editar/eliminar
 */
const formatMoney = (n, moneda) => {
    const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
    return symbol + (parseFloat(n) || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const VariacionesModal = ({
    open,
    onClose,
    base = 0,
    variaciones = [],
    onChange,
    titulo = 'Variaciones de precio',
    moneda = 'PEN',
    readOnly = false
}) => {
    const [formOpen, setFormOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    // Ordenadas por `orden` para que la lista visible respete el orden real
    const ordenadas = useMemo(
        () => [...variaciones].sort((a, b) => (a.orden || 0) - (b.orden || 0)),
        [variaciones]
    );

    // Total final acumulado (aplicando todas las variaciones)
    const totalFinal = aplicarVariaciones(base, ordenadas);

    // Base para el sub-modal: si estamos editando, la base es "resultado post-
    // todas las variaciones anteriores a esta". Si agregamos nueva, es el total
    // acumulado actual.
    const baseParaFormModal = useMemo(() => {
        if (editingIndex == null) return totalFinal;
        const previas = ordenadas.slice(0, editingIndex);
        return aplicarVariaciones(base, previas);
    }, [editingIndex, ordenadas, base, totalFinal]);

    const handleAgregar = () => {
        setEditingIndex(null);
        setFormOpen(true);
    };

    const handleEditar = (idx) => {
        setEditingIndex(idx);
        setFormOpen(true);
    };

    const handleEliminar = (idx) => {
        const nuevo = [...ordenadas];
        nuevo.splice(idx, 1);
        // Re-numerar orden secuencial
        nuevo.forEach((v, i) => { v.orden = i; });
        onChange(nuevo);
    };

    const handleFormSave = (variacion) => {
        let nuevo;
        if (editingIndex != null) {
            nuevo = [...ordenadas];
            nuevo[editingIndex] = { ...nuevo[editingIndex], ...variacion };
        } else {
            nuevo = [...ordenadas, { ...variacion, orden: ordenadas.length }];
        }
        // Re-numerar por si acaso
        nuevo.forEach((v, i) => { v.orden = i; });
        onChange(nuevo);
        setFormOpen(false);
        setEditingIndex(null);
    };

    // Calcular el resultado despues de cada variacion, para mostrar en la lista
    const conAcumulado = [];
    let acc = base;
    for (const v of ordenadas) {
        const antes = acc;
        acc = aplicarVariaciones(antes, [v]);
        conAcumulado.push({ v, antes, despues: acc });
    }

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                title={titulo}
                size="md"
                footer={<Button variant="ghost" size="sm" onClick={onClose}>Cerrar</Button>}
            >
                <div className="variaciones-modal">
                    <div className="variaciones-header">
                        <div className="variaciones-base">
                            <span className="variaciones-base-label">Base:</span>
                            <span className="variaciones-base-value">{formatMoney(base, moneda)}</span>
                        </div>
                        {!readOnly && (
                            <Button
                                variant="soft"
                                size="sm"
                                leftIcon={<Plus size={14} />}
                                onClick={handleAgregar}
                            >
                                Agregar variación
                            </Button>
                        )}
                    </div>

                    {ordenadas.length === 0 ? (
                        <div className="variaciones-empty">
                            No hay variaciones aplicadas. {!readOnly && 'Agregá una para comenzar.'}
                        </div>
                    ) : (
                        <div className="variaciones-list">
                            {conAcumulado.map(({ v, despues }, idx) => {
                                const isDesc = v.tipo === 'descuento';
                                const Icon = isDesc ? TrendingDown : TrendingUp;
                                return (
                                    <div key={idx} className={`variacion-item ${isDesc ? 'variacion-item-danger' : 'variacion-item-success'}`}>
                                        <div className="variacion-item-icon">
                                            <Icon size={14} />
                                        </div>
                                        <div className="variacion-item-body">
                                            <div className="variacion-item-line">
                                                <span className="variacion-item-desc">
                                                    {isDesc ? '– ' : '+ '}
                                                    {v.modalidad === 'porcentaje'
                                                        ? `${v.valor}%`
                                                        : formatMoney(v.valor, moneda)}
                                                </span>
                                                {v.etiqueta && (
                                                    <span className="variacion-item-etiqueta">· {v.etiqueta}</span>
                                                )}
                                            </div>
                                            <div className="variacion-item-resultado">
                                                Después: {formatMoney(despues, moneda)}
                                            </div>
                                        </div>
                                        {!readOnly && (
                                            <div className="variacion-item-actions">
                                                <button
                                                    type="button"
                                                    className="variacion-item-btn"
                                                    onClick={() => handleEditar(idx)}
                                                    aria-label="Editar variación"
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="variacion-item-btn variacion-item-btn-danger"
                                                    onClick={() => handleEliminar(idx)}
                                                    aria-label="Eliminar variación"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="variaciones-total">
                        <span className="variaciones-total-label">Total final:</span>
                        <span className="variaciones-total-value">{formatMoney(totalFinal, moneda)}</span>
                    </div>
                </div>
            </Modal>

            <VariacionFormModal
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditingIndex(null); }}
                base={baseParaFormModal}
                variacion={editingIndex != null ? ordenadas[editingIndex] : null}
                onSave={handleFormSave}
                moneda={moneda}
            />
        </>
    );
};

export default VariacionesModal;
